import React, { FC, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMtsModal } from '@mts-ds/components-react';

import { Dispatch, iRootState } from '../../../../store';
import { guids } from '../../../Root/paths';
import isSatisfied from '../../../../utils/isSatisfied';
import { paths } from '../../../Root/paths';
import { INotificationSetting } from '../../../../models/types';
import { useAsync, useRequestHandler, useParams } from '../../../../hooks';
import { IGetTemplateAllParams } from '../../../../services/NotificationsService';

import Spinner from '../../../../components_/Spinner';
import Pagination from '../../../../components_/Pagination';
import EmptyState from '../../../../components_/EmptyState';
import Modal from '../../../../components_/Modal';

import Filter from './Filter';
import List from './List';

const initialFilter: Omit<IGetTemplateAllParams, 'pageIndex'> = {
  notificationType: '',
  notificationTarget: '',
  employeesList: ''
};

const mapState = (state: iRootState) => ({
  data: state.settingNotifications.data,
  list: state.settingNotifications.list,
  hasRightsToCreate: isSatisfied({ uid: guids.settings.notifications.create, action: 'Visibility' }, state.securable.data)
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getData: d.settingNotifications.getData,
    getList: d.settingNotifications.getList,
    remove: d.settingNotifications.remove,
    reset: d.settingNotifications.reset
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export type IProps = connectedProps;

const Index: FC<IProps> = ({
  data,
  list: { data: list, pagesCount },
  getData,
  getList,
  remove,
  reset,
  hasRightsToCreate
}) => {
  const [active, setActive] = useState<INotificationSetting | null>(null);

  const history = useHistory();
  const requestHandler = useRequestHandler();

  const [runGetData, isLoadingGetData, isGetDataLoadingError] = useAsync(getData);
  const [runGetList, isLoadingGetList, isGetListLoadingError] = useAsync(getList);
  const { pageIndex, filter, handlePageChange, handleFilterChange, handleFilterReset } = useParams(
    initialFilter,
    runGetList
  );

  const updateList = () => {
    getList({ ...filter, pageIndex });
  };

  const [openDeleteModal] = useMtsModal(Modal, {
    header: 'Удалить шаблон',
    text: 'Вы действительно хотите удалить шаблон уведомления?',
    buttonTextConfirm: 'Удалить',
    ...(active && {
      onMtsModalConfirm: async () => {
        const error = await remove(active.id);

        requestHandler({
          error,
          entity: 'notification',
          isDelete: true,
          onSuccess: updateList
        });
      }
    })
  });

  useEffect(() => {
    runGetData();

    return () => {
      reset();
    };
  }, []);

  const handleClickCreate = () => {
    if (hasRightsToCreate) {
      history.push(paths.settings.notifications.create);
    }
  };

  const handleClickEdit = ({ id }: INotificationSetting) => () => {
    history.push(paths.settings.notifications.edit(id));
  };

  const handleClickDelete = (item: INotificationSetting) => () => {
    setActive(item);
    openDeleteModal();
  };

  const isLoading = isLoadingGetList || isLoadingGetData;
  const isLoadingError = isGetListLoadingError || isGetDataLoadingError;

  return (
    <>
      <>
        <Filter
          data={data}
          filter={filter}
          onChange={handleFilterChange}
          onClickCreate={handleClickCreate}
          onReset={handleFilterReset}
          hasRightsToCreate={hasRightsToCreate}
        />
        {!isLoading && !isGetDataLoadingError && (
          <>
            {!list && <EmptyState variant="generateReport" />}
            {list && (
              <>
                {!Boolean(list.length) && <EmptyState variant="emptyRequest" />}
                {Boolean(list.length) && (
                  <>
                    <List
                      data={data}
                      list={list}
                      onClickEdit={handleClickEdit}
                      onClickDelete={handleClickDelete}
                    />
                    <Pagination
                      pageIndex={pageIndex}
                      pagesCount={pagesCount}
                      onPageChange={handlePageChange}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </>
      {isLoading && <Spinner />}
      {isLoadingError && <EmptyState variant="serverError" />}
    </>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Index);
